import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import LocaleLink from "@/lib/i18n/locale-link";
import { cn, formatDate, formatPrice, getCurrencySymbol } from "@/lib/utils";
import { getCurrentLangInClient, useRouterPush } from "@/lib/utils.client";
import { type OrderDetailItem, OrderStatusEnum, type UserOrder } from "@/modules/order/schema";
import { useTracking } from "@/track/tracking-hooks";
import { getTranslatedText } from "../../cms/utils.server";
import OrderCountdown from "./order-countdown";
import { ListOrderStatus } from "./order-status";

// 组件 props 类型
interface OrderItemProps {
  order: UserOrder;
  onCancel?: (orderNumber: string) => void;
  cancelPending?: boolean;
  onOrderExpired?: () => void;
  isOldOrder?: boolean;
}
const UserOrderItem: React.FC<OrderItemProps> = t0 => {
  const $ = _c(141);
  const {
    order,
    onCancel,
    onOrderExpired,
    isOldOrder: t1
  } = t0;
  const isOldOrder = t1 === undefined ? false : t1;
  const {
    t
  } = useTranslation();
  const routerPush = useRouterPush();
  const lang = getCurrentLangInClient();
  const {
    track,
    TrackingEventType
  } = useTracking();
  let t2;
  if ($[0] !== order.orderNumber || $[1] !== routerPush) {
    t2 = function onViewDetail() {
      routerPush(`/order/${order.orderNumber}`);
    };
    $[0] = order.orderNumber;
    $[1] = routerPush;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const onViewDetail = t2;
  let t3;
  if ($[3] !== onCancel || $[4] !== order.orderNumber) {
    t3 = function handleCancel() {
      onCancel?.(order.orderNumber);
    };
    $[3] = onCancel;
    $[4] = order.orderNumber;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleCancel = t3;
  let t4;
  if ($[6] !== TrackingEventType.CLICK || $[7] !== order.orderNumber || $[8] !== routerPush || $[9] !== track) {
    t4 = function onPay() {
      track("PopMartGlobalWebcontinuePaidClickPay", {
        product_type: "mall",
        order_id: order.orderNumber
      }, TrackingEventType.CLICK);
      routerPush(`/payment?orderNumber=${order.orderNumber}`);
    };
    $[6] = TrackingEventType.CLICK;
    $[7] = order.orderNumber;
    $[8] = routerPush;
    $[9] = track;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const onPay = t4;
  const quantity = order.items?.length || 0;
  const map = new Map();
  order.items?.forEach(item => {
    if (!item.sku || !item.spu) {
      return;
    }
    if (!map.has(item.skuId)) {
      map.set(item.skuId, {
        count: 1,
        skuId: item.skuId,
        spuId: item.spuId,
        sku: item.sku,
        spu: item.spu
      });
    } else {
      const existing = map.get(item.skuId);
      if (existing) {
        existing.count = existing.count + 1;
      }
    }
  });
  const skuMap = map;
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = cn("no-border border-b-[#ddd]", "md:border md:border-b-[#ddd]");
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = cn("bg-[#f8f8f8]  font-['DINPro-Regular'] font-normal text-base leading-[1.3] text-[#010101]", "flex items-center justify-between h-16 px-6 gap-6");
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t) {
    t7 = t("order.item.orderTimeLabel");
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== order.createdAt) {
    t8 = formatDate(order.createdAt);
    $[15] = order.createdAt;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7 || $[18] !== t8) {
    t9 = <span className="text-nowrap">{t7} {t8}</span>;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t) {
    t10 = t("order.item.orderNumberLabel");
    $[20] = t;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== order.orderNumber || $[23] !== t10) {
    t11 = <span className="text-nowrap">{t10} {order.orderNumber}</span>;
    $[22] = order.orderNumber;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11 || $[26] !== t9) {
    t12 = <div className="flex gap-x-3 xl:gap-x-4 2xl:gap-x-5 3xl:gap-x-6 flex-wrap">{t9}{t11}</div>;
    $[25] = t11;
    $[26] = t9;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = cn("font-['DINPro-Medium'] font-medium text-base text-[#010101] leading-4 text-left normal-case flex gap-4", "text-nowrap");
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== quantity || $[30] !== t) {
    t14 = t("order.item.itemCountAndTotal", {
      quantity
    });
    $[29] = quantity;
    $[30] = t;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t14) {
    t15 = <span>{t14}</span>;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== order.currency) {
    t16 = getCurrencySymbol(order.currency);
    $[34] = order.currency;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  const t17 = order.payAmount + (order.freightAmount || 0);
  let t18;
  if ($[36] !== order.currency || $[37] !== t17) {
    t18 = formatPrice(order.currency, t17);
    $[36] = order.currency;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t16 || $[40] !== t18) {
    t19 = <span>{t16}{t18}</span>;
    $[39] = t16;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t15 || $[43] !== t19) {
    t20 = <div className={t13}>{t15}{t19}</div>;
    $[42] = t15;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t12 || $[46] !== t20) {
    t21 = <div className={t6}>{t12}{t20}</div>;
    $[45] = t12;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  let t23;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = cn("box-border", "flex items-center justify-between p-6 gap-4");
    t23 = cn("overflow-x-auto w-[22.4vw] max-w-[430px]", "flex gap-3 max-w-107.5");
    $[48] = t22;
    $[49] = t23;
  } else {
    t22 = $[48];
    t23 = $[49];
  }
  let t25;
  if ($[50] !== order) {
    t25 = <ListOrderStatus order={order} />;
    $[50] = order;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== onOrderExpired || $[53] !== order.createdAt || $[54] !== order.status || $[55] !== t25) {
    t26 = <div className="flex flex-col items-end"><OrderCountdown orderStatus={order.status} createdAt={order.createdAt} onOrderExpired={onOrderExpired} onExpiredRefetch={onOrderExpired} displayMode="desktop" fallbackStatus={t25} /></div>;
    $[52] = onOrderExpired;
    $[53] = order.createdAt;
    $[54] = order.status;
    $[55] = t25;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] !== onPay || $[58] !== order.status || $[59] !== t) {
    t27 = order.status === OrderStatusEnum.WAIT_PAY && <Button size="sm" variant="destructive" className={cn("text-xs uppercase", "w-33")} onClick={onPay}>{t("order.item.payNowButton")}</Button>;
    $[57] = onPay;
    $[58] = order.status;
    $[59] = t;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = cn("text-xs border-[#ddd] border bg-transparent uppercase ring-0 shadow-none", "w-33");
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== t) {
    t29 = t("order.item.viewDetailsButton");
    $[62] = t;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] !== onViewDetail || $[65] !== t29) {
    t30 = <Button variant="secondary" size="sm" className={t28} onClick={onViewDetail}>{t29}</Button>;
    $[64] = onViewDetail;
    $[65] = t29;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  let t31;
  if ($[67] !== handleCancel || $[68] !== order.status || $[69] !== t) {
    t31 = order.status === OrderStatusEnum.WAIT_PAY && <Button size="sm" className={cn("text-xs  border bg-transparent uppercase ring-0 shadow-none", "w-33")} variant="secondary" onClick={handleCancel}>{t("order.item.cancelOrderButton")}</Button>;
    $[67] = handleCancel;
    $[68] = order.status;
    $[69] = t;
    $[70] = t31;
  } else {
    t31 = $[70];
  }
  let t32;
  if ($[71] !== t27 || $[72] !== t30 || $[73] !== t31) {
    t32 = <div className="flex flex-col items-center gap-2">{t27}{t30}{t31}</div>;
    $[71] = t27;
    $[72] = t30;
    $[73] = t31;
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  let t33;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = cn("bg-[#f8f8f8] cursor-pointer", "block md:hidden");
    $[75] = t33;
  } else {
    t33 = $[75];
  }
  let t34;
  let t35;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = cn("border-b border-b-[#ddd]", "flex items-center justify-between p-2  ");
    t35 = cn("text-[#111] font-medium text-nowrap", "text-xs");
    $[76] = t34;
    $[77] = t35;
  } else {
    t34 = $[76];
    t35 = $[77];
  }
  let t36;
  if ($[78] !== t) {
    t36 = t("order.item.orderNumberLabel");
    $[78] = t;
    $[79] = t36;
  } else {
    t36 = $[79];
  }
  let t37;
  if ($[80] !== order.orderNumber || $[81] !== t36) {
    t37 = <div className={t35}>{t36} {order.orderNumber}</div>;
    $[80] = order.orderNumber;
    $[81] = t36;
    $[82] = t37;
  } else {
    t37 = $[82];
  }
  let t38;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = cn("font-medium text-nowrap", "text-xs");
    $[83] = t38;
  } else {
    t38 = $[83];
  }
  let t39;
  if ($[84] !== order) {
    t39 = <ListOrderStatus order={order} />;
    $[84] = order;
    $[85] = t39;
  } else {
    t39 = $[85];
  }
  let t40;
  if ($[86] !== onOrderExpired || $[87] !== order.createdAt || $[88] !== order.status || $[89] !== t39) {
    t40 = <div className={t38}><OrderCountdown orderStatus={order.status} createdAt={order.createdAt} onOrderExpired={onOrderExpired} onExpiredRefetch={onOrderExpired} displayMode="mobile" fallbackStatus={t39} /></div>;
    $[86] = onOrderExpired;
    $[87] = order.createdAt;
    $[88] = order.status;
    $[89] = t39;
    $[90] = t40;
  } else {
    t40 = $[90];
  }
  let t41;
  if ($[91] !== t37 || $[92] !== t40) {
    t41 = <div className={t34}>{t37}{t40}</div>;
    $[91] = t37;
    $[92] = t40;
    $[93] = t41;
  } else {
    t41 = $[93];
  }
  const t42 = "p-2";
  let t43;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = cn("overflow-x-auto", "flex gap-2");
    $[94] = t43;
  } else {
    t43 = $[94];
  }
  const t45 = Array.from(skuMap.values()).map(t44 => {
    const {
      skuId: skuId_0,
      spuId: spuId_0,
      sku: sku_0,
      spu: spu_0
    } = t44;
    return isOldOrder ? <div key={skuId_0} className="shrink-0"><Image src={sku_0.mainImage || ""} alt={getTranslatedText(lang, sku_0.name_trans) || getTranslatedText(lang, spu_0.name_trans) || t("order.item.productImageAlt")} width={80} height={80} className={cn("object-contain border border-[#ddd] rounded", "w-20 h-20")} /></div> : <div key={skuId_0} className="shrink-0" onClick={e => {
      e.stopPropagation();
      routerPush(`/products/${spuId_0}`);
    }}><Image src={sku_0.mainImage || ""} alt={getTranslatedText(lang, sku_0.name_trans) || getTranslatedText(lang, spu_0.name_trans) || t("order.item.productImageAlt")} width={80} height={80} className={cn("object-contain border border-[#ddd] rounded", "w-20 h-20")} /></div>;
  });
  let t46;
  if ($[95] !== t43 || $[96] !== t45) {
    t46 = <div className={t42}><div className={t43}>{t45}</div></div>;
    $[95] = t43;
    $[96] = t45;
    $[97] = t46;
  } else {
    t46 = $[97];
  }
  let t47;
  let t48;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = cn("flex items-center justify-between p-2 pt-0");
    t48 = cn("text-xs");
    $[98] = t47;
    $[99] = t48;
  } else {
    t47 = $[98];
    t48 = $[99];
  }
  let t49;
  if ($[100] !== t) {
    t49 = t("order.item.mobile.dateLabel");
    $[100] = t;
    $[101] = t49;
  } else {
    t49 = $[101];
  }
  let t50;
  if ($[102] !== order.createdAt) {
    let t51;
    if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
      t51 = /\//g;
      $[104] = t51;
    } else {
      t51 = $[104];
    }
    t50 = new Date(order.createdAt).toLocaleDateString("zh-CN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    }).replace(t51, ".");
    $[102] = order.createdAt;
    $[103] = t50;
  } else {
    t50 = $[103];
  }
  let t51;
  if ($[105] !== t49 || $[106] !== t50) {
    t51 = <div className={t48}>{t49}{t50}</div>;
    $[105] = t49;
    $[106] = t50;
    $[107] = t51;
  } else {
    t51 = $[107];
  }
  let t52;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = cn("text-gray-900  ", "text-xs");
    $[108] = t52;
  } else {
    t52 = $[108];
  }
  let t53;
  if ($[109] !== order.currency || $[110] !== order.freightAmount || $[111] !== order.payAmount || $[112] !== quantity || $[113] !== t) {
    t53 = t("order.item.mobile.totalLabel", {
      quantity,
      currencySymbol: getCurrencySymbol(order.currency),
      amount: formatPrice(order.currency, order.payAmount + (order.freightAmount || 0))
    });
    $[109] = order.currency;
    $[110] = order.freightAmount;
    $[111] = order.payAmount;
    $[112] = quantity;
    $[113] = t;
    $[114] = t53;
  } else {
    t53 = $[114];
  }
  let t54;
  if ($[115] !== order.currency) {
    t54 = getCurrencySymbol(order.currency);
    $[115] = order.currency;
    $[116] = t54;
  } else {
    t54 = $[116];
  }
  const t55 = order.payAmount + (order.freightAmount || 0);
  let t56;
  if ($[117] !== order.currency || $[118] !== t55) {
    t56 = formatPrice(order.currency, t55);
    $[117] = order.currency;
    $[118] = t55;
    $[119] = t56;
  } else {
    t56 = $[119];
  }
  let t57;
  if ($[120] !== t54 || $[121] !== t56) {
    t57 = <span className="font-medium font-['DINPro-Medium']">{t54}{t56}</span>;
    $[120] = t54;
    $[121] = t56;
    $[122] = t57;
  } else {
    t57 = $[122];
  }
  let t58;
  if ($[123] !== t53 || $[124] !== t57) {
    t58 = <div className={t52}>{t53}{" - "}{t57}</div>;
    $[123] = t53;
    $[124] = t57;
    $[125] = t58;
  } else {
    t58 = $[125];
  }
  let t59;
  if ($[126] !== t51 || $[127] !== t58) {
    t59 = <div className={t47}>{t51}{t58}</div>;
    $[126] = t51;
    $[127] = t58;
    $[128] = t59;
  } else {
    t59 = $[128];
  }
  let t60;
  if ($[129] !== handleCancel || $[130] !== onPay || $[131] !== order.status || $[132] !== t) {
    t60 = order.status === OrderStatusEnum.WAIT_PAY && <div className={cn("  border-t border-t-[#ddd]", "flex  py-3 ")} onClick={_temp}><button type="button" className={cn("text-xs underline leading-none", "flex-1")} onClick={handleCancel}>{t("order.item.cancelOrderButton")}</button><button type="button" className={cn("text-xs text-destructive underline  leading-none font-['DINPro-Bold'] font-bold", "flex-1")} onClick={onPay}>{t("order.item.payNowButton")}</button></div>;
    $[129] = handleCancel;
    $[130] = onPay;
    $[131] = order.status;
    $[132] = t;
    $[133] = t60;
  } else {
    t60 = $[133];
  }
  let t61;
  if ($[134] !== onViewDetail || $[135] !== t33 || $[136] !== t41 || $[137] !== t46 || $[138] !== t59 || $[139] !== t60) {
    t61 = <div className={t33} onClick={onViewDetail}>{t41}{t46}{t59}{t60}</div>;
    $[134] = onViewDetail;
    $[135] = t33;
    $[136] = t41;
    $[137] = t46;
    $[138] = t59;
    $[139] = t60;
    $[140] = t61;
  } else {
    t61 = $[140];
  }
  return <div className={t5}><div className="hidden md:block ">{t21}<div className={t22}><div className={t23}>{Array.from(skuMap.values()).map(t24 => {
            const {
              skuId,
              spuId,
              sku,
              spu
            } = t24;
            return isOldOrder ? <div key={skuId} className="flex flex-col items-center shrink-0"><Image src={sku.mainImage || ""} alt={getTranslatedText(lang, sku.name_trans) || getTranslatedText(lang, spu.name_trans) || t("order.item.productImageAlt")} width={118} height={118} className={cn("object-contain border", "w-29.5 h-29.5")} /></div> : <LocaleLink key={skuId} href={`/products/${spuId}`} className="flex flex-col items-center shrink-0"><Image src={sku.mainImage || ""} alt={getTranslatedText(lang, sku.name_trans) || getTranslatedText(lang, spu.name_trans) || t("order.item.productImageAlt")} width={118} height={118} className={cn("object-contain border", "w-29.5 h-29.5")} /></LocaleLink>;
          })}</div>{t26}{t32}</div></div>{t61}</div>;
};
export default UserOrderItem;
function _temp(e_0) {
  return e_0.stopPropagation();
}